import { Component } from 'react';
import { BaseLinkModel, BaseNodeModel } from '../models';
import { GenericValue, NodeTypeName } from '../types';

interface ContextProps {
  isCommonModule?: boolean;
  isRootModule?: boolean;
  moduleId?: string;
  nodeTypeName?: NodeTypeName;
  parentComponentID?: string;
}

export type Context = {
  props: ContextProps;
  hasWriteProxy?: () => boolean;
  setConfig: (key: string | null, value: GenericValue, xtras?: Xtras) => void;
  getConfig: (key: string, props?: BaseComponentProps) => GenericValue;
  [key: string]: any;
};

type Xtras = Record<string, unknown>;

export type BaseComponentProps = {
  context?: Context;
  valKey?: string;
  value?: GenericValue;
  property?: string;
  [key: string]: any;
};

export default class BaseComponent<Props extends BaseComponentProps = BaseComponentProps, State = unknown> extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
  }

  shouldUpdateParentNode(): boolean {
    if (!this.props.context) {
      return false;
    }
    if (
      !this.props.context.props.nodeTypeName
      || this.props.context.props.nodeTypeName === NodeTypeName.variable
      || this.props.context.props.nodeTypeName === NodeTypeName.intent
      || (
        !this.props.context.hasWriteProxy
        && !this.props.context.props.isCommonModule
      )
    ) {
      return false;
    }
    if (this.props.context.props.nodeTypeName === NodeTypeName.module) {
      const shouldUpdateParent = ['name', 'description', 'colorClass', 'iconClass', 'version'];
      return shouldUpdateParent.includes(this.props.valKey || '');
    }

    return true;
  }

  updateContext(newValue: GenericValue, xtras: Xtras = {}) {
    const valKey = this.props.valKey || null;

    if (typeof newValue === 'string' && newValue === this.props.value) {
      return;
    }

    if (
      !window.apm
      && this.props.context
    ) {
      this.props.context.setConfig(valKey, newValue);
      return;
    }

    const setContextConfig = () => {
      // Special case for front page, outside any single app
      if (!this.props.context || !valKey) {
        return;
      }

      if (
        valKey === 'inputs'
        && (newValue === 'app.' || newValue === 'mod.')
      ) {
        return;
      }
      this.props.context.setConfig(valKey, newValue, xtras);

      if (valKey === 'inputs' && this.props.context.type === 'module' && window.location.hash === '#intents') {
        window.apm.updateGlobalIntent(this.props.context, valKey, newValue);
      }
      if (valKey === 'name' && this.props.context.type === 'module' && window.location.hash === '#intents') {
        window.apm.updateGlobalIntent(this.props.context, valKey, newValue);
        const module = window.apm.serialized.components.modules.find((m) => {
          return m.id === this.props.context!.props.parentComponentID;
        });
        if (module && module.props.isCommonModule && module.props.isRootModule) {
          window.apm.commonModuleManager.renameModuleApp(module.props.moduleId, newValue);
        }
      }
      // CM TODO: should not run when upgrading CoMo version, see ModuleNodeModel.changeCommonModuleVersion
      if (this.shouldUpdateParentNode()) {
        const nodeId = (this.props.context.hasWriteProxy!()) ? this.props.context.id : this.props.context.props.parentComponentID;
        window.apm.updateParentNode(this.props.context, nodeId, valKey, newValue, this.props.context.type, this.props.value);
      }

      if (valKey === 'name' && this.props.context.props.isCommonModule && this.props.context.props.isRootModule) {
        window.apm.commonModuleManager.renameModuleApp(this.props.context.props.moduleId, newValue);
      }

      // update member tree
      const treeM = $('#tm_container');
      // TODO: how does this work? ltree gets update on mod name change, but this mtree looks for `name`, not `Name`
      if (this.props.label && this.props.valKey === 'name' && this.props.label.toLowerCase() === 'name' && treeM) {
        treeM.jstree().rename_node(this.props.context.getID(), newValue);
      }

      if (this.props.context.hasWriteProxy && !this.props.context.hasWriteProxy()) {
        // Refresh canvas selectively for performance
        if (this.props.context instanceof BaseNodeModel) {
          window.apm.postEventToCurrentView({ target: 'request-refresh', event: { id: this.props.context.getID(), type: 'node', upDatedField: valKey, props: this.props.context.props, newValue: newValue } });
        } else if (this.props.context instanceof BaseLinkModel) {
          window.apm.postEventToCurrentView({ target: 'request-refresh-links', event: { id: this.props.context.getID(), type: 'link', updatedLink: this.props.context } });
        } else {
          window.apm.postEventToCurrentView({ target: 'request-refresh' });
        }
      }
    };

    setContextConfig();
  }

  getContextValue(): GenericValue {
    if (!this.props.context) {
      return null;
    }
    if (this.props.valKey === 'externalAudioServiceUrl' || this.props.valKey === 'externalAudioPrompt') {
      return this.props.context.getConfig(this.props.valKey, this.props);
    }
    if (this.props.property && this.props.property === 'inputs') {
      return this.props.context.getConfig('inputs', this.props);
    }
    return this.props.context.getConfig(this.props.valKey ?? '');
  }

  getPropLabel(key: keyof typeof window.l.form): string {
    return window.l.form[key];
  }

  render() {
    return <div />;
  }
}
